import { useMemo, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const key = (r, c) => `${r}-${c}`;
const cellsOf = (e) => Array.from({ length: e.length ?? e.answer?.length ?? 0 }, (_, i) => (e.direction === "across" ? [e.row, e.col + i] : [e.row + i, e.col]));

function Clues({ entries, res, answers, children }) {
  const { t } = useI18n();
  return ["across", "down", undefined].map((d) => {
    const list = entries.filter((e) => e.direction === d);
    if (!list.length) return null;
    return (
      <div key={d || "list"} className="space-y-1" data-testid={`cw-clues-${d || "list"}`}>
        {d && <div className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500">{t(`al.p.${d}`)}</div>}
        {list.map((e) => (
          <div key={e.id} className="text-sm flex flex-wrap items-center gap-2" data-testid={`cw-clue-${e.id}`}>
            <b dir="ltr">{e.number}.</b> <span dir="auto">{e.clue}</span> <span className="text-gray-500" dir="ltr">({e.length})</span>
            {res && (res.results[e.id] ? <Check className="w-4 h-4 text-green-700" /> : <X className="w-4 h-4 text-coral" />)}
            {children?.(e)}
            {answers?.[e.id] && <span className="text-xs text-gray-600" data-testid={`cw-key-${e.id}`}>✓ <span dir="auto">{answers[e.id].join(" / ")}</span></span>}
          </div>
        ))}
      </div>
    );
  });
}

// Grid for Latin/Cyrillic/Greek; Arabic-native numbered clue puzzle for clue_list layouts (no forced letter grid).
export default function Crossword({ cw, language, onCheck, answers }) {
  const { t } = useI18n();
  const [val, setVal] = useState({});
  const [res, setRes] = useState(null);
  const [active, setActive] = useState("across");
  const refs = useRef({});
  const entries = useMemo(() => cw.entries.map((e) => ({ ...e, length: e.length ?? [...(e.answer || "")].length })), [cw]);
  const cells = useMemo(() => {
    const m = {};
    if (cw.layout === "grid") entries.forEach((e) => cellsOf(e).forEach(([r, c]) => { m[key(r, c)] = m[key(r, c)] || { r, c, dirs: [] }; m[key(r, c)].dirs.push(e.direction); }));
    entries.forEach((e) => { const s = m[key(e.row, e.col)]; if (s) { s.n = e.number; s.starts = [...(s.starts || []), e.direction]; } });
    return m;
  }, [cw, entries]);
  const word = (e) => (cw.layout === "grid" ? cellsOf(e).map(([r, c]) => val[key(r, c)] || "").join("") : val[e.id] || "");
  const check = async () => setRes(await onCheck(Object.fromEntries(entries.map((e) => [e.id, word(e)]))));
  const type = (r, c, ch) => {
    setVal({ ...val, [key(r, c)]: ch.slice(-1) });
    if (!ch) return;
    const dirs = cells[key(r, c)].dirs, d = dirs.includes(active) ? active : dirs[0];
    const dir = d === "across" ? [0, 1] : [1, 0];
    refs.current[key(r + dir[0], c + dir[1])]?.focus();
  };
  return (
    <div className="space-y-4" data-testid="asset-crossword" lang={language}>
      {cw.layout === "grid" ? (
        <div className="overflow-x-auto max-w-full" dir="ltr">
          <div className="inline-grid gap-0 border-2 border-ink" style={{ gridTemplateColumns: `repeat(${cw.cols}, 2.25rem)` }} data-testid="cw-grid">
            {Array.from({ length: cw.rows * cw.cols }, (_, i) => {
              const r = Math.floor(i / cw.cols), c = i % cw.cols, cell = cells[key(r, c)];
              if (!cell) return <div key={i} className="w-9 h-9 bg-ink" />;
              return (
                <label key={i} className="relative w-9 h-9 border border-ink/40 bg-white">
                  {cell.n && <span className="absolute top-0 start-0.5 text-[9px] leading-none">{cell.n}</span>}
                  <input ref={(el) => { refs.current[key(r, c)] = el; }} value={val[key(r, c)] || ""} onChange={(e) => type(r, c, e.target.value)} onFocus={() => { const d = cell.starts?.length === 1 ? cell.starts[0] : cell.dirs.length === 1 ? cell.dirs[0] : null; if (d) setActive(d); }}
                    onMouseDown={(e) => { if (document.activeElement === e.target && cell.dirs.length > 1) setActive(active === "across" ? "down" : "across"); }}
                    className="w-full h-full text-center uppercase font-bold bg-transparent outline-none focus:bg-butter" maxLength={2} aria-label={`${r + 1},${c + 1}`} data-testid={`cw-cell-${r}-${c}`} />
                </label>
              );
            })}
          </div>
        </div>
      ) : <p className="text-xs text-gray-600" data-testid="cw-cluelist-note">{t("al.p.clueListNote")}</p>}
      <Clues entries={entries} res={res} answers={answers}>
        {cw.layout === "grid" ? null : (e) => (
          <input className="nb-input py-1 w-40 ms-auto" dir="auto" maxLength={40} value={val[e.id] || ""} onChange={(x) => setVal({ ...val, [e.id]: x.target.value })} disabled={!onCheck} data-testid={`cw-input-${e.id}`} />
        )}
      </Clues>
      {onCheck && (
        <div className="flex items-center gap-3">
          <button onClick={check} className="nb-btn-primary py-2 px-4" data-testid="cw-check">{t("al.check")}</button>
          {res && <span className="text-sm font-bold" data-testid="cw-score">{t("al.score", { c: res.correct, t: res.total })}</span>}
          {res && <button onClick={() => { setRes(null); setVal({}); }} className="text-sm underline" data-testid="cw-retry">{t("al.tryAgain")}</button>}
        </div>
      )}
    </div>
  );
}
